import React, { useCallback, useEffect, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import {
  ChevronLeft, MapPin, Tag, Calendar, Euro, Send, Archive,
  Trash2, Pencil, MessageSquare, CheckCircle2, Clock, AlertTriangle,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import {
  freelanceGigsApi,
  FreelanceGig,
  FreelanceGigResponseWithEmployee,
} from '../services/freelanceGigsApi';
import './FreelanceGigs.scss';

/**
 * Employer-side detail page for one of their own gigs.
 * Shows the gig metadata + list of responders. Clicking a responder
 * navigates to the dedicated thread page (`/freelance/messages/:respId`).
 */
const FreelanceGigDetail: React.FC = () => {
  const { user, isAuthenticated, isLoading } = useAuth();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [gig, setGig] = useState<FreelanceGig | null>(null);
  const [responses, setResponses] = useState<FreelanceGigResponseWithEmployee[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionBusy, setActionBusy] = useState(false);

  const gigId = id ? parseInt(id, 10) : NaN;

  const load = useCallback(async () => {
    if (Number.isNaN(gigId)) return;
    setLoading(true);
    try {
      const data = await freelanceGigsApi.responses(gigId);
      setGig(data.gig);
      setResponses(data.responses);
    } catch (err: any) {
      setActionError(err?.message || 'Nepavyko įkelti Gig');
    } finally {
      setLoading(false);
    }
  }, [gigId]);

  useEffect(() => {
    if (isAuthenticated && user?.user_type === 'employer') load();
  }, [isAuthenticated, user, load]);

  if (isLoading || loading) return <div className="freelance-page-loading">Kraunama...</div>;
  if (!isAuthenticated) return <Navigate to="/login?type=employer&mode=employer" replace />;
  if (user?.user_type !== 'employer') return <Navigate to="/" replace />;
  if (!gig) {
    return (
      <div className="freelance-create-page">
        <button className="freelance-back-link" onClick={() => navigate('/freelance/gigs?mode=employer')}>
          <ChevronLeft size={16} /> Atgal
        </button>
        <div className="freelance-alert freelance-alert--error">
          {actionError || 'Gig nerastas'}
        </div>
      </div>
    );
  }

  const runAction = async (fn: () => Promise<unknown>) => {
    setActionBusy(true);
    setActionError(null);
    try {
      await fn();
      await load();
    } catch (err: any) {
      setActionError(err?.message || 'Veiksmas nepavyko');
    } finally {
      setActionBusy(false);
    }
  };

  const onPublish = () => runAction(() => freelanceGigsApi.publish(gig.id));
  const onClose = () => {
    if (!window.confirm('Ar tikrai uždaryti šį Gig?')) return;
    return runAction(() => freelanceGigsApi.close(gig.id));
  };
  const onDelete = async () => {
    if (!window.confirm('Ar tikrai ištrinti šį Gig? Veiksmas negrįžtamas.')) return;
    setActionBusy(true);
    try {
      await freelanceGigsApi.remove(gig.id);
      navigate('/freelance/gigs?mode=employer');
    } catch (err: any) {
      setActionError(err?.message || 'Nepavyko ištrinti');
      setActionBusy(false);
    }
  };

  const interestedCount = responses.filter((r) => r.status === 'interested').length;

  return (
    <div className="freelance-detail-page">
      <button className="freelance-back-link" onClick={() => navigate('/freelance/gigs?mode=employer')}>
        <ChevronLeft size={16} /> Visi Gigai
      </button>

      <div className="freelance-detail-header">
        <div>
          <div className={`freelance-gig-status freelance-gig-status--${gig.status}`}>
            {gig.status === 'published' ? <><CheckCircle2 size={14} />Paskelbta</> :
             gig.status === 'closed'    ? <><Archive size={14} />Uždaryta</> :
                                          <><Clock size={14} />Juodraštis</>}
          </div>
          <h1 className="freelance-detail-title">{gig.title}</h1>
        </div>

        <div className="freelance-detail-actions">
          {gig.status === 'draft' && (
            <>
              <Link
                to={`/freelance/gigs/${gig.id}/edit?mode=employer`}
                className="btn btn-secondary"
              >
                <Pencil size={14} /> Redaguoti
              </Link>
              <button className="btn btn-primary" disabled={actionBusy} onClick={onPublish}>
                <Send size={14} /> Paskelbti
              </button>
            </>
          )}
          {gig.status === 'published' && (
            <button className="btn btn-secondary" disabled={actionBusy} onClick={onClose}>
              <Archive size={14} /> Uždaryti
            </button>
          )}
          <button className="btn btn-danger" disabled={actionBusy} onClick={onDelete}>
            <Trash2 size={14} /> Ištrinti
          </button>
        </div>
      </div>

      {actionError && (
        <div className="freelance-alert freelance-alert--error">
          <AlertTriangle size={16} /> {actionError}
        </div>
      )}

      <div className="freelance-detail-grid">
        <section className="freelance-card">
          <h2>Aprašymas</h2>
          <p className="freelance-detail-desc">{gig.description}</p>

          <div className="freelance-detail-meta">
            {gig.work_fields.length > 0 && (
              <div className="freelance-meta-row"><Tag size={14} />
                <strong>Sritys:</strong> {gig.work_fields.join(', ')}
              </div>
            )}
            <div className="freelance-meta-row"><MapPin size={14} />
              <strong>Vieta:</strong> {gig.nationwide ? 'Visoje Lietuvoje' : (gig.city || 'Nenurodyta')}
            </div>
            {gig.deadline_text && (
              <div className="freelance-meta-row"><Calendar size={14} />
                <strong>Terminas:</strong> {gig.deadline_text}
              </div>
            )}
            {gig.budget_text && (
              <div className="freelance-meta-row"><Euro size={14} />
                <strong>Biudžetas:</strong> {gig.budget_text}
              </div>
            )}
          </div>
        </section>

        <section className="freelance-card">
          <h2>Susidomėję ({interestedCount})</h2>
          {responses.length === 0 ? (
            <p className="freelance-empty-hint">
              {gig.status === 'published'
                ? 'Dar nėra susidomėjusių. Mes jau išsiuntėme laiškus tinkamiems specialistams.'
                : 'Paskelbus Gig, čia atsiras susidomėjusių sąrašas.'}
            </p>
          ) : (
            <ul className="freelance-responder-list">
              {responses.map((r) => {
                const name = [r.employee_first_name, r.employee_last_name]
                  .filter(Boolean).join(' ').trim() || 'Kandidatas';
                return (
                  <li key={r.id} className={`freelance-responder freelance-responder--${r.status}`}>
                    <div className="freelance-responder-info">
                      <strong>{name}</strong>
                      <span className="freelance-responder-status">
                        {r.status === 'interested' ? 'Susidomėjo' : 'Atsisakė'}
                      </span>
                      {r.intro_message && (
                        <p className="freelance-responder-intro">„{r.intro_message}"</p>
                      )}
                    </div>
                    <Link
                      to={`/freelance/messages/${r.id}?mode=employer`}
                      className="btn btn-secondary btn-sm freelance-thread-btn"
                    >
                      <MessageSquare size={14} /> Žinutės
                      {r.unread_count > 0 && (
                        <span className="freelance-unread-dot" aria-label="Neperskaitytos žinutės">
                          {r.unread_count}
                        </span>
                      )}
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
};

export default FreelanceGigDetail;
